import { useState, useEffect } from "react";
import { useMutation, useQueryClient, useQuery } from "@tanstack/react-query";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import { ChevronDown, Search, X } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { useToast } from "@/hooks/use-toast";
import { apiRequest } from "@/lib/queryClient";
import { insertPerformerSchema, instrumentOptions, tierOptions } from "@shared/schema";
import { cn } from "@/lib/utils";
import { useBrowserDetection } from "@/hooks/use-browser-detection";
import type { Performer, Participant, JamNight } from "@shared/schema";

const formSchema = z.object({
  participantId: z.number().optional(),
  name: z.string().min(1, "Name is required"),
  email: z.string().email("Invalid email address").optional().or(z.literal("")),
  phone: z.string().optional(),
  instagram: z.string().optional(),
  instruments: z.array(z.string()).min(1, "Please select at least one instrument"),
  tier: z.string().optional(),
  priority: z.boolean().optional().default(false),
  lookingForBand: z.boolean().optional().default(false),
  notes: z.string().optional(),
});

interface AddParticipantModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  jamNightId: number;
}

export default function AddParticipantModal({ open, onOpenChange, jamNightId }: AddParticipantModalProps) {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const [selectedParticipant, setSelectedParticipant] = useState<Participant | null>(null);
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [showDropdown, setShowDropdown] = useState<boolean>(false);

  // Detect Chrome browsers for different transparency treatment
  const { isChrome, isMobileSafari } = useBrowserDetection();

  // Fetch jam night details to get organisation ID
  const { data: jamNight } = useQuery({
    queryKey: [`/api/events/${jamNightId}`],
    queryFn: async () => {
      const response = await apiRequest("GET", `/api/events/${jamNightId}`);
      return response.json();
    },
    enabled: open,
  });

  // Fetch all participants for the organisation
  const { data: participants = [] } = useQuery({
    queryKey: [`/api/participants?organisationId=${jamNight?.organisationId}`],
    queryFn: async () => {
      const response = await apiRequest("GET", `/api/participants?organisationId=${jamNight?.organisationId}`);
      const result = await response.json();
      return result;
    },
    enabled: open && !!jamNight?.organisationId,
  });

  // Fetch existing performers for this event to check for duplicates
  const { data: existingPerformers = [] } = useQuery({
    queryKey: [`/api/performers?jamNightId=${jamNightId}`],
    queryFn: async () => {
      const response = await apiRequest("GET", `/api/performers?jamNightId=${jamNightId}`);
      return response.json();
    },
    enabled: open,
  });

  // Get set of participant IDs that are already added to this event
  const existingParticipantIds = new Set(
    existingPerformers
      .filter((p: Performer) => p.participantId && !p.removed)
      .map((p: Performer) => p.participantId)
  );

  // Filter participants based on search term
  const filteredParticipants = (() => {
    if (!participants) return [];
    
    if (searchTerm.trim().length === 0) {
      return participants.sort((a: Participant, b: Participant) => a.name.localeCompare(b.name));
    }
    
    return participants
      .filter((participant: Participant) => 
        participant.name.toLowerCase().includes(searchTerm.toLowerCase())
      )
      .sort((a: Participant, b: Participant) => a.name.localeCompare(b.name));
  })();

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      participantId: undefined,
      name: "",
      email: "",
      phone: "",
      instagram: "",
      instruments: [],
      tier: "",
      priority: false,
      lookingForBand: false,
      notes: "",
    },
  });

  const createPerformerMutation = useMutation({
    mutationFn: async (data: z.infer<typeof formSchema>) => {
      // Get the current performers to determine the new sort order
      const currentPerformers = await apiRequest("GET", `/api/performers?jamNightId=${jamNightId}`).then(res => res.json());
      
      // Find the maximum sort order among existing performers
      const maxSortOrder = currentPerformers.reduce((max: number, p: Performer) => {
        return p.sortOrder !== undefined ? Math.max(max, p.sortOrder) : max;
      }, 0);
      
      const performerData = { 
        ...data, 
        jamNightId,
        contact: "", // Keep empty contact field for backward compatibility
        sortOrder: maxSortOrder + 1000 // Add a large gap to avoid conflicts with future reordering
      };

      const response = await apiRequest("POST", "/api/performers", performerData);
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [`/api/performers?jamNightId=${jamNightId}`] });
      // Also invalidate participants query to update autocomplete
      if (jamNight?.organisationId) {
        queryClient.invalidateQueries({ queryKey: [`/api/participants?organisationId=${jamNight.organisationId}`] });
      }
      form.reset();
      setSelectedParticipant(null);
      onOpenChange(false);
    },
    onError: (error: any) => {
      // Check if the error is a duplicate participant error
      if (error.message === "This participant is already added to this event") {
        toast({
          title: "Duplicate Participant",
          description: "This participant is already added to this event",
          variant: "destructive",
        });
      } else {
        toast({
          title: "Error",
          description: "Failed to add participant",
          variant: "destructive",
        });
      }
    },
  });

  const onSubmit = (data: z.infer<typeof formSchema>) => {
    createPerformerMutation.mutate(data);
  };

  const handleParticipantSelect = (participant: Participant) => {
    setSelectedParticipant(participant);
    form.setValue("participantId", participant.id);
    form.setValue("name", participant.name);
    form.setValue("email", participant.email || "");
    form.setValue("phone", participant.phone || "");
    form.setValue("instagram", participant.instagram || "");
    form.setValue("lookingForBand", participant.lookingForBand || false);

    // Fetch and pre-populate instruments from most recent performer record
    const fetchRecentInstruments = async () => {
      try {
        const response = await apiRequest("GET", `/api/performers/recent/${participant.id}`);
        const recentPerformer = await response.json();
        
        if (recentPerformer && recentPerformer.instruments && recentPerformer.instruments.length > 0) {
          form.setValue("instruments", recentPerformer.instruments);
          // Also pre-populate tier and priority if they exist
          if (recentPerformer.tier) {
            form.setValue("tier", recentPerformer.tier);
          }
          form.setValue("priority", recentPerformer.priority || false);
        } else {
          console.log("No instruments found in recent performer or no recent performer");
        }
      } catch (error: any) {
        // apiRequest throws for 404, which is expected for participants with no recent performer
        if (error.message && error.message.includes("404")) {
          console.log("No recent performer found for participant (404), using defaults");
        } else {
          console.log("Error fetching recent performer:", error);
        }
      }
    };

    fetchRecentInstruments();
  };

  const handleSearchChange = (value: string) => {
    setSearchTerm(value);
    setShowDropdown(true);
    form.setValue("name", value);
  };

  const handleClearSelection = () => {
    setSelectedParticipant(null);
    setSearchTerm("");
    setShowDropdown(false);
    form.setValue("participantId", undefined);
    form.setValue("name", "");
    form.setValue("email", "");
    form.setValue("phone", "");
    form.setValue("instagram", "");
    form.setValue("instruments", []);
    form.setValue("tier", "");
    form.setValue("priority", false);
    form.setValue("lookingForBand", false);
    form.setValue("notes", "");
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = () => {
      setShowDropdown(false);
    };
    
    if (showDropdown) {
      document.addEventListener('click', handleClickOutside);
      return () => document.removeEventListener('click', handleClickOutside);
    }
  }, [showDropdown]);

  // Reset state when modal opens
  useEffect(() => {
    if (open) {
      setSelectedParticipant(null);
      setSearchTerm("");
      setShowDropdown(false);
      form.reset();
    }
  }, [open, form]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="w-[95vw] max-w-md max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Add New Participant</DialogTitle>
        </DialogHeader>
        
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Participant Name</FormLabel>
                  <FormControl>
                    <div className="relative">
                      <div className="relative" onClick={(e) => e.stopPropagation()}>
                        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-slate-400" />
                        <Input
                          value={searchTerm}
                          onChange={(e) => handleSearchChange(e.target.value)}
                          onFocus={() => setShowDropdown(true)}
                          onClick={() => setShowDropdown(true)}
                          placeholder="Search for participant or enter new name..."
                          className="pl-10 pr-10"
                        />
                        {searchTerm && (
                          <button
                            type="button"
                            onClick={handleClearSelection}
                            className="absolute right-3 top-1/2 transform -translate-y-1/2 text-slate-400 hover:text-slate-600"
                          >
                            <X className="h-4 w-4" />
                          </button>
                        )}
                      </div>

                      {/* Dropdown Results */}
                      {showDropdown && filteredParticipants.length > 0 && (
                        <div 
                          className={`absolute z-50 w-full mt-1 rounded-xl shadow-2xl max-h-60 overflow-auto border ${
                            isChrome || isMobileSafari
                              ? 'bg-white border-gray-200' 
                              : 'bg-white/20 backdrop-blur-xl border-white/30'
                          }`}
                          onClick={(e) => e.stopPropagation()}
                          style={{ 
                            backgroundColor: (isChrome || isMobileSafari) ? 'white' : undefined,
                            backdropFilter: (isChrome || isMobileSafari) ? 'none' : undefined
                          }}
                        >
                          {filteredParticipants.map((participant: Participant) => {
                            const isAlreadyAdded = existingParticipantIds.has(participant.id);
                            return (
                              <div
                                key={participant.id}
                                onClick={() => {
                                  if (!isAlreadyAdded) {
                                    handleParticipantSelect(participant);
                                    setSearchTerm(participant.name);
                                    setShowDropdown(false);
                                  }
                                }}
                                className={cn(
                                  "px-4 py-3 border-b border-white/30 last:border-b-0",
                                  isAlreadyAdded 
                                    ? "opacity-50 cursor-not-allowed bg-white/20" 
                                    : "hover:bg-white/40 cursor-pointer"
                                )}
                              >
                                <div className="flex items-center justify-between">
                                  <div className="flex-1">
                                    <div className="font-medium text-slate-900 flex items-center gap-2">
                                      {participant.name}
                                      {isAlreadyAdded && (
                                        <Badge variant="outline" className="text-xs bg-red-50 text-red-700 border-red-200">
                                          Already added
                                        </Badge>
                                      )}
                                    </div>
                                    {participant.email && (
                                      <div className="text-sm text-slate-500">
                                        {participant.email}
                                      </div>
                                    )}
                                  </div>
                                  <div className="flex items-center space-x-2">
                                    {participant.lookingForBand && (
                                      <Badge className="bg-blue-100 text-blue-800 text-xs">
                                        Looking for Band
                                      </Badge>
                                    )}
                                  </div>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="instruments"
              render={() => (
                <FormItem>
                  <FormLabel>Instruments</FormLabel>
                  <div className="grid grid-cols-2 gap-2">
                    {instrumentOptions.map((instrument) => (
                      <FormField
                        key={instrument}
                        control={form.control}
                        name="instruments"
                        render={({ field }) => (
                          <FormItem className="flex items-center space-x-2">
                            <FormControl>
                              <Checkbox
                                checked={field.value?.includes(instrument)}
                                onCheckedChange={(checked) => {
                                  const value = field.value || [];
                                  if (checked) {
                                    field.onChange([...value, instrument]);
                                  } else {
                                    field.onChange(value.filter((item) => item !== instrument));
                                  }
                                }}
                              />
                            </FormControl>
                            <FormLabel className="text-sm capitalize font-normal">
                              {instrument}
                            </FormLabel>
                          </FormItem>
                        )}
                      />
                    ))}
                  </div>
                  <FormMessage />
                </FormItem>
              )}
            />

            <Collapsible className="space-y-4">
              <CollapsibleTrigger className="flex items-center space-x-2 text-sm font-medium">
                <ChevronDown className="h-4 w-4" />
                <span>Contact Details</span>
              </CollapsibleTrigger>
              <CollapsibleContent className="space-y-4">
                <FormField
                  control={form.control}
                  name="email"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Email Address</FormLabel>
                      <FormControl>
                        <Input type="email" placeholder="Enter email address" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="phone"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Mobile Number</FormLabel>
                      <FormControl>
                        <Input type="tel" placeholder="Enter mobile number" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="instagram"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Instagram Handle</FormLabel>
                      <FormControl>
                        <Input placeholder="Enter Instagram handle" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </CollapsibleContent>
            </Collapsible>

            <FormField
              control={form.control}
              name="tier"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Tier (Optional)</FormLabel>
                  <Select onValueChange={field.onChange} defaultValue={field.value}>
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder="Select a tier" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {tierOptions.map((tier) => (
                        <SelectItem key={tier} value={tier}>
                          {tier}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="priority"
              render={({ field }) => (
                <FormItem className="flex flex-row items-center space-x-3 space-y-0">
                  <FormControl>
                    <Checkbox
                      checked={field.value}
                      onCheckedChange={field.onChange}
                    />
                  </FormControl>
                  <div className="space-y-1 leading-none">
                    <FormLabel>Priority Participant</FormLabel>
                    <p className="text-sm text-muted-foreground">
                      Mark this participant as priority
                    </p>
                  </div>
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="lookingForBand"
              render={({ field }) => (
                <FormItem className="flex flex-row items-center space-x-3 space-y-0">
                  <FormControl>
                    <Checkbox
                      checked={field.value}
                      onCheckedChange={field.onChange}
                    />
                  </FormControl>
                  <div className="space-y-1 leading-none">
                    <FormLabel>Looking for a Band</FormLabel>
                    <p className="text-sm text-muted-foreground">
                      This participant is looking to join or form a band
                    </p>
                  </div>
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="notes"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Notes (Optional)</FormLabel>
                  <FormControl>
                    <Textarea
                      placeholder="Add any notes about this participant for this event..."
                      className="min-h-[80px] resize-none"
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <div className="flex space-x-3 pt-4">
              <Button
                type="button"
                variant="outline"
                className="flex-1"
                onClick={() => onOpenChange(false)}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                className="flex-1 bg-blue-600/80 backdrop-blur-sm text-white hover:bg-blue-700/90 border border-blue-500/30 shadow-lg transition-all duration-300"
                style={{
                  boxShadow: '0 4px 16px rgba(59,130,246,0.2), inset 0 1px 0 rgba(255,255,255,0.3)',
                }}
                disabled={createPerformerMutation.isPending}
              >
                {createPerformerMutation.isPending ? "Adding..." : "Add Participant"}
              </Button>
            </div>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
